import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { OrderedMap, Map } from 'immutable';

import Booking from 'components/bookings/past/booking';
import Header from 'components/bookings/past/header';
import TableHeadings from 'components/bookings/past/table-headings';
import Filter from 'components/common/filter';
import FilterOption from 'components/common/filter/option';
import ButtonToggle from 'components/common/button-toggle';
import Toggle from 'components/common/button-toggle/toggle';
import ModalWrapper from 'components/bookings/modal-wrapper';
import Paginate from 'components/common/paginate';

import Brand from 'models/brand';
import BookingsGroup from 'models/bookings-group';

export const PAST_BOOKINGS_LIMIT = 10;

const ALL_FILTER = 'allBookings';
const BUSINESS_FILTER = 'businessBookings';
const REFUNDED_FILTER = 'refundedBookings';

const TRANSIENT_FILTER = 'transient';
const MONTHLY_FILTER = 'monthly';

const pastBookingShape = PropTypes.shape({
  transient: PropTypes.instanceOf(BookingsGroup),
  monthly: PropTypes.instanceOf(BookingsGroup),
});

const propTypes = {
  app: PropTypes.string.isRequired,
  brand: PropTypes.instanceOf(Brand).isRequired,
  fetchPastBookings: PropTypes.func.isRequired,
  locations: PropTypes.instanceOf(OrderedMap),
  reviewBooking: PropTypes.func.isRequired,
  reviews: PropTypes.instanceOf(Map).isRequired,
  trackEvent: PropTypes.func.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  allBookings: pastBookingShape.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  businessBookings: pastBookingShape.isRequired,
  // eslint-disable-next-line react/no-unused-prop-types
  refundedBookings: pastBookingShape.isRequired,
};

const defaultProps = {
  locations: OrderedMap(),
};

class Past extends Component {
  constructor(props) {
    super(props);

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);
    this.selectPurchaseTypeFilter = this.selectPurchaseTypeFilter.bind(this);
    this.selectBookingTypeFilter = this.selectBookingTypeFilter.bind(this);
    this.handlePageClick = this.handlePageClick.bind(this);

    this.state = {
      openModal: false,
      purchaseTypeFilter: this.initialPurchaseTypeFilter,
      bookingTypeFilter: this.initialBookingTypeFilter(this.initialPurchaseTypeFilter),
    };
  }

  get initialPurchaseTypeFilter() {
    const { allBookings } = this.props;

    return allBookings.hasBookings ? ALL_FILTER : REFUNDED_FILTER;
  }

  get selectedBookingsGroup() {
    const { purchaseTypeFilter, bookingTypeFilter } = this.state;

    return this.props[purchaseTypeFilter][bookingTypeFilter];
  }

  get bookingCountForSelectedGroup() {
    return this.selectedBookingsGroup.totalCount;
  }

  get bookingQuery() {
    const { bookingTypeFilter, purchaseTypeFilter } = this.state;

    const query = [];

    switch (purchaseTypeFilter) {
      case ALL_FILTER:
        query.push('past', 'active');
        break;
      case BUSINESS_FILTER:
        query.push('past', 'businessPurchase', 'active');
        break;
      case REFUNDED_FILTER:
        query.push('cancelled');
        break;
      default:
        break;
    }

    if (bookingTypeFilter === MONTHLY_FILTER) {
      query.push('monthly');
    }

    return query;
  }

  get modalBooking() {
    const { allBookings } = this.props;
    const { openModal } = this.state;

    return openModal ? (allBookings.transient[openModal] || allBookings.monthly[openModal]) : null;
  }

  get shouldShowFilter() {
    const { allBookings, businessBookings, refundedBookings } = this.props;

    return (allBookings.hasBookings && refundedBookings.hasBookings) || businessBookings.hasBookings;
  }

  initialBookingTypeFilter(purchaseTypeFilter) {
    return this.props[purchaseTypeFilter].transient.bookings.size ? TRANSIENT_FILTER : MONTHLY_FILTER;
  }

  openModal(key) {
    this.setState({
      openModal: key.toString(),
    });
  }

  closeModal() {
    this.setState({
      openModal: false,
    });
  }

  handlePageClick(page) {
    const { purchaseTypeFilter, bookingTypeFilter } = this.state;
    this.props.fetchPastBookings({
      bookingRequest: { page, perPage: PAST_BOOKINGS_LIMIT },
      bookingsQuery: this.bookingQuery,
      purchaseType: purchaseTypeFilter,
      bookingType: bookingTypeFilter,
    });
  }

  selectPurchaseTypeFilter({ filter }) {
    this.setState({ purchaseTypeFilter: filter, bookingTypeFilter: this.initialBookingTypeFilter(filter) });
  }

  selectBookingTypeFilter(bookingTypeFilter) {
    this.setState({ bookingTypeFilter });
  }

  renderBookings() {
    const { app, brand, reviews, trackEvent } = this.props;

    let bookingNodes = this.selectedBookingsGroup.bookings.toIndexedSeq();

    bookingNodes = bookingNodes.map((booking, index) => {
      const location = booking && booking.getLocation ? booking.getLocation(this.props.locations) : null;
      const review = reviews.get(booking.id.toString());

      return (<Booking
        key={booking.id}
        index={index}
        booking={booking}
        location={location}
        disabled={Boolean(booking.cancelledAt)}
        openModal={this.openModal}
        brand={brand}
        review={review}
        reviewBooking={this.props.reviewBooking}
        app={app}
        trackEvent={trackEvent}
        analyticsPosition={index}
      />);
    });

    return bookingNodes;
  }

  renderBookingTypeFilter() {
    const { purchaseTypeFilter } = this.state;

    if (this.props[purchaseTypeFilter].hasMonthlyAndTransient) {
      return (
        <ButtonToggle className="booking-type-toggle" onClick={this.selectBookingTypeFilter} selectedToggle={this.state.bookingTypeFilter}>
          <Toggle toggleValue={TRANSIENT_FILTER}>Hourly</Toggle>
          <Toggle toggleValue={MONTHLY_FILTER}>Monthly</Toggle>
        </ButtonToggle>
      );
    }

    return null;
  }

  renderPagination() {
    const { paginationCurrentPage, paginationTotalPages } = this.selectedBookingsGroup;

    if (paginationTotalPages < 2) return null;

    return (
      <Fragment>
        <div className="hidden-xs hidden-sm pull-right">
          <Paginate
            maxMargin={4}
            onClick={this.handlePageClick}
            selectedPage={paginationCurrentPage}
            totalPages={paginationTotalPages}
          />
        </div>
        <div className="hidden-md hidden-lg pull-right">
          <Paginate
            maxMargin={2}
            onClick={this.handlePageClick}
            selectedPage={paginationCurrentPage}
            totalPages={paginationTotalPages}
          />
        </div>
      </Fragment>
    );
  }

  render() {
    const { brand, reviews } = this.props;
    const { purchaseTypeFilter } = this.state;
    const { modalBooking } = this;

    let modalLocation;
    let modalReview;
    if (modalBooking) {
      modalReview = reviews.get(modalBooking.id.toString());
      if (modalBooking.getLocation) {
        modalLocation = modalBooking.getLocation(this.props.locations);
      }
    }

    // We always keep the modal component rendered, because it improves performance
    return (
      <div className="booking-section past-bookings">
        <a className="nav-offset-anchor" id="past-bookings" />
        <Header count={this.bookingCountForSelectedGroup} />
        <Filter
          displayTest={() => this.shouldShowFilter}
          currentFilter={purchaseTypeFilter}
          onFilter={this.selectPurchaseTypeFilter}
        >
          {this.props.allBookings.hasBookings && <FilterOption filter={ALL_FILTER}>ALL</FilterOption>}
          {
            this.props.businessBookings.hasBookings &&
              <FilterOption filter={BUSINESS_FILTER}>
                <span className="iconified-font iconified-briefcase" />&nbsp;BUSINESS
              </FilterOption>
          }
          {this.props.refundedBookings.hasBookings && <FilterOption filter={REFUNDED_FILTER}>REFUNDED</FilterOption>}
        </Filter>
        {this.renderBookingTypeFilter()}
        <TableHeadings />
        {this.renderBookings()}
        {this.renderPagination()}
        <ModalWrapper
          display={this.state.openModal}
          booking={modalBooking}
          location={modalLocation}
          close={this.closeModal}
          brand={brand}
          review={modalReview}
          reviewBooking={this.props.reviewBooking}
        />
      </div>
    );
  }
}

Past.propTypes = propTypes;
Past.defaultProps = defaultProps;

export default Past;
